// TD Draft 2026-09-28 G2-P01 — 'Say what changed when a choice isn't available' for the TD variant picker
// (block setting explain_unavailable; markup: snippets/td-p01-data.liquid + snippets/td-p01-option-note.liquid).
// Loaded before assets/custom.js (layout/theme.liquid): its applyUnavailableFilter hands these pickers to tdP01.filter.
//
// - Pickers with the sold-out filter and a complete variant list (data.keep):
//   (a) a selected value that does not exist / is sold out with the options above it is replaced (as before, the
//       sold-out filter never leaves an unavailable selection): the weight by the nearest available weight (tie → the
//       lighter one, never rounded up), other options by the first available value in DOM order (as before). One
//       role=status line under that option says what changed, naming only the options above that make it true
//       ('7 kg gibt es in 100 x 150 cm nicht.', '6 kg ist in 100 x 150 cm mit Schlaufen gerade ausverkauft.').
//   (b) the line offers a one-tap route back when the old value exists in stock elsewhere ('Zurück zu 110 x 170 cm mit 7 kg').
//   (c) sold-out SIZES stay visible, crossed out and not selectable (disabled: no notify form on an in-stock product,
//       the server line in the slot names them and links colours that have them in stock). Every other sold-out value
//       is hidden as before. Sold-out sizes never count as a choice: a size group with one size in stock keeps D06's
//       compact row / Q23's static step (custom.js isStatic skips disabled inputs), with the server line under it.
// - Pickers without the filter (set: 400 combinations, the Liquid variant list is capped at 250): when the buyer's change
//   leads to a sold-out or missing combination, one line says so, with a route back to the previous value only (a cover
//   must match the blanket the buyer owns, so no other option is ever switched for them).
// - The line goes away with the buyer's next choice. Prestige swaps the picker on every change, so notices are kept per
//   form id and put back after each swap; a new line is filled 150ms after it is inserted, so screen readers announce it
//   once (re-inserted copies after a swap are filled at once and stay quiet).
// - TD Draft 2026-09-28 G2: one box, not two. When the server line in the same slot names the value the line is about
//   (a link with ?variant= to a sold-out size: '150 x 220 cm ist in Grau gerade ausverkauft' + colour links), the line
//   takes over that sentence and those links ('… Wir haben 135 x 200 cm gewählt.' is added) and the server line hides.
(() => {
  if (window.tdP01) return;

  const state = new Map(); /* form id → { sel, selOk, change, notices: Map(position → notice) } */
  /* TD Draft 2026-10-01 I18N — the sentences come from the locales (td.p01.*) as data-t-* attributes of the data script
     (snippets/td-p01-data.liquid); [name] is filled in, an empty value takes one neighbouring space with it */
  const T = {};
  const readStrings = (script) => {
    Object.keys(script.dataset).forEach((name) => {
      if (/^t[A-Z]/.test(name) && !(name in T)) T[name] = script.dataset[name];
    });
  };
  const t = (name, vars = {}) => {
    let s = T[name] || '';
    Object.keys(vars).forEach((k) => {
      const token = `[${k}]`;
      const v = vars[k] == null ? '' : String(vars[k]);
      s = v ? s.split(token).join(v) : s.split(` ${token}`).join('').split(token).join('');
    });
    return s;
  };
  let autoDispatch = 0; /* > 0 while this file dispatches its own change (auto-selection) */

  const cssEsc = (v) => (window.CSS && CSS.escape ? CSS.escape(String(v)) : String(v).replace(/["\\]/g, '\\$&'));
  const formIdOf = (container) => container.dataset.formId || container.getAttribute('form-id') || '';
  const stateOf = (container) => {
    const id = formIdOf(container);
    let st = state.get(id);
    if (!st) {
      st = { sel: null, selOk: true, change: null, notices: new Map() };
      state.set(id, st);
    }
    return st;
  };
  const dataOf = (container) => {
    const script = container.querySelector(':scope > script[data-td-p01-data]');
    if (!script) return null;
    if (script.tdP01Data === undefined) {
      try { script.tdP01Data = JSON.parse(script.textContent); } catch (e) { script.tdP01Data = null; }
      readStrings(script); /* TD Draft 2026-10-01 I18N */
    }
    return script.tdP01Data;
  };
  const optionCount = (container, data) => {
    const fromData = data && data.v && data.v.length ? data.v.reduce((m, v) => Math.max(m, v.o.length), 0) : 0;
    return fromData || container.querySelectorAll('fieldset[data-td-option-position]').length;
  };
  const checked = (container) => {
    const sel = {};
    container.querySelectorAll('input[data-option-position]:checked').forEach((input) => {
      sel[input.dataset.optionPosition] = input.dataset.optionValue || '';
    });
    return sel;
  };
  const selKey = (sel) => [1, 2, 3].map((p) => sel[p] || '').join('|');

  const kg = (value) => {
    const m = String(value || '').match(/(\d+(?:[.,]\d+)?)\s*kg/i);
    return m ? parseFloat(m[1].replace(',', '.')) : NaN;
  };
  const isSize = (value) => /\d+\s*x\s*\d+/i.test(String(value || ''));
  /* position of the size option: from Liquid (option name contains 'größe'), otherwise the option whose values read '100 x 150 cm' */
  const sizePos = (data) => {
    if (data.s) return data.s;
    const first = data.v && data.v[0];
    const i = first ? first.o.findIndex(isSize) : -1;
    return i + 1;
  };
  /* 'in 100 x 150 cm' · 'mit Reißverschluss' · 'mit 7 kg' · 'in der Variante „Medizinisch (ohne Bezug)“' */
  const prep = (value) => {
    if (isSize(value)) return t('tIn', { value });
    if (/schlaufen|reißverschluss|\d\s*kg\b/i.test(value)) return t('tWith', { value });
    return t('tVariant', { value });
  };

  const variantFor = (data, sel, n) => data.v.find((v) => {
    for (let p = 1; p <= n; p++) if (v.o[p - 1] !== sel[p]) return false;
    return true;
  }) || null;

  /* body-weight range of a variant; kids Reißverschluss / Medizinisch variants carry no metafield: borrow it from a
     variant with the same size and weight (same rule as the D07 weight helper) */
  const rangeOf = (data, variant) => {
    if (!variant) return '';
    if (variant.r) return variant.r;
    const w = data.w - 1;
    const s = variant.o.findIndex(isSize);
    const twin = data.v.find((x) => x.r && x.o[w] === variant.o[w] && (s < 0 || x.o[s] === variant.o[s]));
    return twin ? twin.r : '';
  };

  /* replacement for an unavailable value: nearest weight (tie → lighter), otherwise the first value in DOM order */
  const pick = (data, pos, current, pool, wrappers) => {
    if (pos === data.w) {
      const target = kg(current);
      if (!Number.isNaN(target)) {
        let best = null;
        let bestDiff = Infinity;
        let bestKg = Infinity;
        pool.forEach((value) => {
          const k = kg(value);
          if (Number.isNaN(k)) return;
          const diff = Math.abs(k - target);
          if (diff < bestDiff || (diff === bestDiff && k < bestKg)) {
            best = value;
            bestDiff = diff;
            bestKg = k;
          }
        });
        if (best) return best;
      }
    }
    for (const wrapper of wrappers) {
      if (pool.has(wrapper.dataset.optionVal)) return wrapper.dataset.optionVal;
    }
    return pool.values().next().value || null;
  };

  /* TD Draft 2026-09-26 D06 rule: a group with one selectable value collapses to 'Legend: value' (a crossed-out,
     disabled sold-out size is visible but no choice, so it does not count) */
  const applySingle = (container, pos, wrappers) => {
    const fieldset = container.querySelector(`fieldset[data-td-option-position="${pos}"]`);
    if (!fieldset || !wrappers.length) return;
    const visible = wrappers.filter((w) => w.style.display !== 'none' && !w.classList.contains('td-p01-soldout'));
    /* TD Draft 2026-09-28 D06-SINGLE-OFF — Felix 28.09.2026 „show all swatches even when only one option available - like it was before“:
       no collapse to 'Legend: value' any more; a group with one selectable value keeps its card(s) as on live. */
    const isSingle = false && visible.length === 1;
    /* TD Draft 2026-09-28 D06-SINGLE-OFF — lone visible card (a crossed-out size counts, it takes a place): card width, not the full row */
    fieldset.classList.toggle('td-option--lone', wrappers.filter((w) => w.style.display !== 'none').length === 1);
    fieldset.classList.toggle('td-option--single', isSingle);
    let label = fieldset.querySelector('[data-td-single-value]');
    const legend = fieldset.querySelector('legend');
    const next = legend ? legend.nextElementSibling : null;
    const hasValueSpan = !!next && next.tagName === 'SPAN' && next !== label;
    if (isSingle && !hasValueSpan && !fieldset.querySelector('[data-td-option-selected-value]')) {
      if (!label) {
        label = document.createElement('span');
        label.className = 'td-variant-picker__single-value';
        label.setAttribute('data-td-single-value', '');
        if (legend) legend.after(label);
      }
      label.textContent = visible[0].dataset.optionVal || '';
    } else if (!isSingle && label) {
      label.remove();
    }
  };

  /* ---------- the line (notice) ---------- */
  const clearNotices = (st) => {
    st.notices.forEach((notice) => clearTimeout(notice.timer));
    st.notices.clear();
  };

  /* TD Draft 2026-09-28 G2 — the server line this notice folds into: fresh, in the same slot, naming notice.from */
  const foldOf = (slot, notice, fresh) => {
    if (!fresh || !notice.from) return null;
    return [...slot.querySelectorAll(':scope > [data-td-p01-server][data-td-p01-names]')]
      .find((s) => s.dataset.tdP01Names.split('|').includes(notice.from)) || null;
  };
  const textOf = (el, notice) => {
    const fold = el.tdP01Fold;
    const said = fold && fold.querySelector('p') ? fold.querySelector('p').textContent.replace(/\s+/g, ' ').trim() : '';
    return said ? `${said}${notice.tail ? ` ${notice.tail}` : ''}` : notice.text;
  };

  const fill = (el, notice) => {
    const status = el.querySelector('[role="status"]');
    const text = textOf(el, notice); /* TD Draft 2026-09-28 G2 */
    if (status && status.textContent !== text) status.textContent = text;
    if (notice.route && !el.querySelector('[data-td-p01-route]')) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'td-p01-note__route';
      button.dataset.tdP01Route = JSON.stringify(notice.route.sel);
      button.textContent = notice.route.label;
      el.append(button);
    }
    /* TD Draft 2026-09-28 G2 — the folded server line's links come along, after the route */
    const alts = el.tdP01Fold && el.tdP01Fold.querySelector('.td-p01-note__alts');
    if (alts && !el.querySelector('.td-p01-note__alts')) el.append(alts.cloneNode(true));
    el.classList.add('is-filled');
  };

  const schedule = (notice) => {
    if (notice.timer || notice.announced) return;
    notice.timer = setTimeout(() => {
      notice.timer = 0;
      notice.announced = true;
      document.querySelectorAll(`[data-td-p01-notice][data-key="${cssEsc(notice.key)}"]`).forEach((el) => fill(el, notice));
    }, 150);
  };

  const build = (notice) => {
    const el = document.createElement('div');
    el.className = 'td-p01-note td-p01-note--changed';
    el.setAttribute('data-td-p01-notice', '');
    el.dataset.key = notice.key;
    if (notice.missing) el.setAttribute('data-td-p01-missing', '');
    const status = document.createElement('p');
    status.setAttribute('role', 'status');
    el.append(status);
    return el;
  };

  /* put notices and the freshness of the server lines in line with the picker on screen */
  const render = (container) => {
    const st = stateOf(container);
    const sel = checked(container);
    const key = selKey(sel);
    container.querySelectorAll('[data-td-p01-slot]').forEach((slot) => {
      const pos = slot.dataset.tdP01Slot;
      /* a server line rendered for another selection (client auto-selection before the swap) waits for the swap */
      const fresh = slot.dataset.tdP01For === key;
      const notice = st.notices.get(Number(pos));
      const on = !!notice && sel[pos] === notice.to;
      const fold = on ? foldOf(slot, notice, fresh) : null; /* TD Draft 2026-09-28 G2: one box, not two */
      slot.querySelectorAll(':scope > [data-td-p01-server]').forEach((el) => { el.hidden = !fresh || el === fold; });
      let el = slot.querySelector(':scope > [data-td-p01-notice]');
      if (!on) {
        if (el) el.remove();
        return;
      }
      if (el && el.dataset.key === notice.key && (el.tdP01Fold || null) === fold) return;
      if (el) el.remove();
      el = build(notice);
      el.tdP01Fold = fold;
      slot.prepend(el);
      if (notice.announced) fill(el, notice);
      else schedule(notice);
    });
  };

  const keepNotice = (st, notice) => {
    if (!notice.text.trim()) return; /* TD Draft 2026-10-01 I18N: no sentences on the page (old cached markup) → no empty box */
    const old = st.notices.get(notice.pos);
    if (old && old.key === notice.key) return;
    if (old) clearTimeout(old.timer);
    st.notices.set(notice.pos, notice);
  };

  /* the options above entry.pos that make the sentence true, as few as possible: the buyer's change first, then the
     size, then the rest; said size first ('in 100 x 150 cm mit Schlaufen'). 'missing' checks existence, 'soldout' stock. */
  const contextFor = (data, entry, selected, change) => {
    const s = sizePos(data);
    const cp = change ? Number(change.pos) : 0;
    const rank = (p) => (p === cp ? 0 : p === s ? 1 : 2);
    const above = [];
    for (let p = 1; p < entry.pos; p++) if (selected[p]) above.push(p);
    above.sort((a, b) => rank(a) - rank(b) || a - b);
    const chosen = [];
    const holds = () => !data.v.some((v) => v.o[entry.pos - 1] === entry.from && (entry.cause === 'missing' || v.a)
      && chosen.every((p) => v.o[p - 1] === selected[p]));
    for (const p of above) {
      if (holds()) break;
      chosen.push(p);
    }
    chosen.sort((a, b) => (a === s ? -1 : b === s ? 1 : a - b));
    return chosen.map((p) => prep(selected[p])).join(' ');
  };

  /* (a) + (b): text and route for one auto-selection */
  const makeNotice = (data, entry, selected, base, change, n, count) => {
    const ctx = contextFor(data, entry, selected, change);
    const said = entry.cause === 'missing'
      ? (ctx ? t('tMissingCtx', { from: entry.from, ctx }) : t('tMissing', { from: entry.from }))
      : (ctx ? t('tSoldOutCtx', { from: entry.from, ctx }) : t('tSoldOut', { from: entry.from }));
    let tail; /* TD Draft 2026-09-28 G2: kept apart, a fold puts it after the server line's sentence */
    if (entry.pos === data.w && !Number.isNaN(kg(entry.to))) {
      const range = rangeOf(data, variantFor(data, selected, n));
      tail = range ? t('tNearest', { to: entry.to, range }) : t('tNearestPlain', { to: entry.to });
    } else {
      tail = t('tChosen', { to: entry.to });
    }
    const text = `${said} ${tail}`;

    let route = null;
    const cp = change ? Number(change.pos) : 0;
    if (cp && cp < entry.pos && count === 1) {
      /* where the old value is in stock with everything else as selected now; the value before the change comes first */
      const places = [];
      data.v.forEach((v) => {
        if (!v.a || v.o[entry.pos - 1] !== entry.from) return;
        for (let p = 1; p <= n; p++) {
          if (p === cp || p === entry.pos) continue;
          if (v.o[p - 1] !== selected[p]) return;
        }
        if (!places.includes(v.o[cp - 1])) places.push(v.o[cp - 1]);
      });
      const back = base[cp] && places.includes(base[cp]);
      const target = back ? base[cp] : places[0];
      if (target) {
        route = {
          sel: { [cp]: target, [entry.pos]: entry.from },
          label: t(back ? 'tBack' : 'tSwitch', { value: `${target} ${prep(entry.from)}` })
        };
      }
    } else if (cp && count > 1 && entry.last) {
      /* several options changed at once: one route back to the whole previous selection, when it is in stock */
      const before = variantFor(data, base, n);
      if (before && before.a) {
        const sel = {};
        const parts = [];
        for (let p = 1; p <= n; p++) { sel[p] = base[p]; parts.push(base[p]); }
        route = { sel, label: t('tBack', { value: parts.join(', ') }) };
      }
    }
    return { key: `a|${entry.pos}|${entry.from}|${entry.to}|${text}`, pos: entry.pos, from: entry.from, to: entry.to, text, tail, route, missing: false, announced: false, timer: 0 };
  };

  /* ---------- filter (called by applyUnavailableFilter in assets/custom.js) ---------- */
  const filter = (container, variant) => {
    const data = dataOf(container);
    if (!data || !data.complete || !data.keep || !data.v || !data.v.length) return false;
    const all = data.v;
    if (!all.some((v) => v.a)) return false; /* fully sold out: custom.js filters nothing (D09) */
    const st = stateOf(container);
    const n = optionCount(container, data);

    const selected = {};
    if (variant && variant.id) {
      for (let p = 1; p <= n; p++) {
        const value = variant[`option${p}`];
        if (value) selected[p] = value;
      }
    } else {
      Object.assign(selected, checked(container));
    }

    const change = st.change;
    const base = (change && change.prev) || st.sel || {};
    const auto = [];

    for (let pos = 1; pos <= n; pos++) {
      const exists = new Set();
      const avail = new Set();
      all.forEach((v) => {
        for (let other = 1; other < pos; other++) {
          if (selected[other] && v.o[other - 1] !== selected[other]) return;
        }
        exists.add(v.o[pos - 1]);
        if (v.a) avail.add(v.o[pos - 1]);
      });

      const wrappers = [...container.querySelectorAll(`[data-option-pos="${pos}"]`)];
      const current = selected[pos];
      const keepSoldOut = pos === sizePos(data); /* (c) sold-out sizes stay visible (crossed out, disabled) */
      let cause = '';
      if (current && exists.size && !exists.has(current)) cause = 'missing';
      else if (current && avail.size && !avail.has(current)) cause = 'soldout'; /* as custom.js: never left unavailable */
      if (cause) {
        const next = pick(data, pos, current, avail.size ? avail : exists, wrappers);
        if (next && next !== current) {
          selected[pos] = next;
          auto.push({ pos, from: current, to: next, cause });
        }
      }

      wrappers.forEach((wrapper) => {
        const value = wrapper.dataset.optionVal;
        if (!value) return;
        const soldOut = keepSoldOut && exists.has(value) && !avail.has(value) && value !== selected[pos];
        if (avail.has(value) || value === selected[pos] || soldOut) wrapper.style.removeProperty('display');
        else wrapper.style.setProperty('display', 'none', 'important');
        wrapper.classList.toggle('td-p01-soldout', soldOut);
        /* TD 2026-10-11 UX-PDP #1 (Codex R1) — the visible word "ausverkauft" (td-ux-so, assets/td-ux-pdp.css) follows the
           same rule as the first paint in snippets/td-variant-picker.liquid: exists here, no available variant, and on
           screen as a kept size or the selected value — so word, dashed card and crossed-out state always agree */
        wrapper.classList.toggle('td-ux-so', exists.has(value) && !avail.has(value) && (keepSoldOut || value === selected[pos]));
        /* not selectable: a sold-out size is no choice (and opens no notify form); the checked radio is never disabled */
        const input = wrapper.querySelector('input[data-option-position]');
        if (input) {
          if (soldOut && !input.disabled) {
            input.disabled = true;
            input.setAttribute('data-td-p01-disabled', '');
          } else if (!soldOut && input.hasAttribute('data-td-p01-disabled')) {
            input.disabled = false;
            input.removeAttribute('data-td-p01-disabled');
          }
        }
        const swatch = wrapper.querySelector('.block-swatch, .color-swatch, .thumbnail-swatch');
        if (swatch) {
          swatch.classList.toggle('is-disabled', !avail.has(value));
          /* the strike-through is visual only: screen readers hear '(ausverkauft)' with the value */
          let sr = swatch.querySelector(':scope > [data-td-p01-sr]');
          if (soldOut && !sr) {
            sr = document.createElement('span');
            sr.className = 'sr-only';
            sr.setAttribute('data-td-p01-sr', '');
            sr.textContent = t('tSr');
            swatch.append(sr);
          } else if (!soldOut && sr) {
            sr.remove();
          }
        }
      });

      applySingle(container, pos, wrappers);
    }

    if (auto.length) {
      auto[auto.length - 1].last = true;
      auto.forEach((entry) => keepNotice(st, makeNotice(data, entry, selected, base, change, n, auto.length)));
      let last = null;
      auto.forEach((entry) => {
        container.querySelectorAll(`input[data-option-position="${entry.pos}"]`).forEach((input) => {
          if (input.dataset.optionValue === entry.to && !input.checked) {
            input.checked = true;
            last = input;
          }
        });
      });
      if (last) {
        autoDispatch += 1;
        try {
          last.dispatchEvent(new Event('change', { bubbles: true }));
        } finally {
          autoDispatch -= 1;
        }
      }
    }

    st.sel = Object.assign({}, selected);
    const now = variantFor(data, selected, n);
    st.selOk = !!(now && now.a);
    render(container);
    return true;
  };

  /* ---------- pickers without the P01 filter (set) + render after every swap ---------- */
  const evaluate = (container) => {
    const data = dataOf(container);
    if (!data) return;
    /* with the sold-out filter the selection is always resolved (tdP01.filter, or custom.js when the list is partial);
       a fully sold-out product (D09) only gets the server line */
    if (container.hasAttribute('data-hide-unavailable') || (data.complete && !data.v.some((v) => v.a))) {
      render(container);
      return;
    }
    const st = stateOf(container);
    const sel = checked(container);
    const n = optionCount(container, data);
    let variant = null;
    const script = container.querySelector(':scope > script[data-variant]');
    try { variant = script ? JSON.parse(script.textContent) : null; } catch (e) { variant = null; }
    if (variant && variant.id) {
      for (let p = 1; p <= n; p++) {
        if ((variant[`option${p}`] || '') !== (sel[p] || '')) return; /* the picker on screen is not the rendered one yet */
      }
    }
    const exists = !!(variant && variant.id);
    const ok = exists && variant.available !== false;
    const change = st.change;
    if (change && !ok && sel[change.pos] === change.value) {
      const missing = !exists;
      let text = t(missing ? 'tComboMissing' : 'tComboSoldOut', { value: change.value });
      if (missing && data.complete && data.v.length) {
        /* where it exists: the other option values it comes with (max. 3), never switched for the buyer */
        const places = [];
        data.v.forEach((v) => {
          if (v.o[change.pos - 1] !== change.value) return;
          const parts = [];
          for (let p = 1; p <= n; p++) if (p !== Number(change.pos) && v.o[p - 1] !== sel[p]) parts.push(prep(v.o[p - 1]));
          const where = parts.join(' ');
          if (where && !places.includes(where)) places.push(where);
        });
        if (places.length && places.length <= 3) text += ` ${t('tOnly', { value: change.value, places: places.join(t('tOr')) })}`;
      }
      const back = change.prev && change.prev[change.pos];
      const route = back && back !== change.value && change.prevOk ? { sel: { [change.pos]: back }, label: t('tBack', { value: back }) } : null;
      keepNotice(st, { key: `b|${change.pos}|${change.value}|${missing}`, pos: Number(change.pos), from: missing ? '' : change.value, to: change.value, text, tail: '', route, missing, announced: false, timer: 0 });
    }
    st.sel = sel;
    st.selOk = ok;
    render(container);
  };

  const pickers = (root) => [...(root || document).querySelectorAll('[data-td-variant-picker]')].filter((c) => dataOf(c));
  const evaluateAll = (root) => pickers(root).forEach(evaluate);

  /* the buyer's own choice: remember what changed (and the selection before it); the line goes away */
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (autoDispatch || !input || !input.matches || !input.matches('input[data-option-position]')) return;
    const container = input.closest('[data-td-variant-picker]');
    if (!container || !dataOf(container)) return;
    const st = stateOf(container);
    st.change = {
      pos: Number(input.dataset.optionPosition),
      value: input.dataset.optionValue || '',
      prev: Object.assign({}, st.sel || {}),
      prevOk: st.selOk
    };
    clearNotices(st);
    render(container);
  }, true); /* capture: before custom.js (quick buy filters synchronously in its own capture listener) */

  /* one-tap route: select the values, keep focus in the option the line belonged to */
  document.addEventListener('click', (event) => {
    const button = event.target.closest ? event.target.closest('[data-td-p01-route]') : null;
    if (!button) return;
    const container = button.closest('[data-td-variant-picker]');
    if (!container) return;
    let sel;
    try { sel = JSON.parse(button.dataset.tdP01Route); } catch (e) { return; }
    const fieldset = button.closest('fieldset');
    let last = null;
    Object.keys(sel).sort().forEach((pos) => {
      const input = [...container.querySelectorAll(`input[data-option-position="${pos}"]`)].find((i) => i.dataset.optionValue === sel[pos]);
      if (input && !input.checked) {
        if (input.hasAttribute('data-td-p01-disabled')) { input.disabled = false; input.removeAttribute('data-td-p01-disabled'); }
        input.checked = true;
        last = input;
      }
    });
    /* focus leaves the button before the line goes: the step header (accordion, same id after the swap, so Prestige
       keeps it), otherwise the chosen value's radio once the swap has made it visible (it may be hidden until then) */
    const own = fieldset && fieldset.querySelector('input[data-option-position]:checked');
    const ownId = own ? own.id : '';
    const header = fieldset && fieldset.querySelector(':scope > .td-acc__header:not([hidden])');
    if (header) header.focus({ preventScroll: true });
    if (last) last.dispatchEvent(new Event('change', { bubbles: true })); /* a buyer action: clears the line */
    if (!header && ownId) {
      let tries = 0;
      const refocus = () => {
        const a = document.activeElement;
        if (a && a !== document.body) return;
        const el = document.getElementById(ownId);
        if (el && el.getClientRects().length && !el.closest('[inert]')) el.focus({ preventScroll: true });
        else if (++tries < 40) setTimeout(refocus, 50);
      };
      refocus();
    }
  });

  /* after Prestige swapped the picker (main product: variant:change; quick buy: product:rerender on a detached form) */
  document.addEventListener('variant:change', () => evaluateAll());
  document.addEventListener('product:rerender', () => queueMicrotask(() => evaluateAll()), true);
  document.addEventListener('dialog:after-show', (event) => {
    if (event.target && event.target.matches && event.target.matches('quick-buy-modal')) evaluateAll(event.target);
  }, true);
  document.addEventListener('shopify:section:load', () => evaluateAll());

  const init = () => pickers().forEach((container) => {
    const st = stateOf(container);
    if (!st.sel) st.sel = checked(container);
    evaluate(container);
  });

  window.tdP01 = { filter, refresh: evaluateAll };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
